// views.tsx · v12.10.30 (#2255 practice offline) · 2026-11-03 · VoiceSpectrum AI
// views.tsx · v12.10.11.1 (#1460 Contact support: already have your request + Add to SR; daily limit reached) · 2026-10-09
// views.tsx · v12.10.11.1 (#1462 Set up your login: names pre-filled, no line under the heading, boxes full width + rules one per line; reset: New + Confirm side by side, bar under New; ✓ in Confirm; wording B) · 2026-10-09
// views.tsx · v12.10.11.1 (#1470 code / reset screens show what was typed, "Didn't receive…?" hint) · 2026-10-09
// views.tsx · v12.10.9.4 (#1342 F9 password rules; #1336 no sample text in sign-in boxes) · 2026-10-07 · VoiceSpectrum AI
// views.tsx · v12.10.9.3 (#1312 the sign-in card ends after the one-time-code buttons; #1311 NEW tag with an end date) · 2026-10-07
// VoiceSpectrum.Web.T2 · v12.10.9.1 (#1270 #1288 #1275) · 2026-10-06
// v12.10.9.1: #1275 "Contact support" opens a short form (SupportView / SupportSentView) instead of a mailto link.
// views.tsx - one component per sign-in state of Login_Prototype_v1_2 (the APPROVED pixel reference). Each view only
// renders and validates its own fields; every API call and every transition lives in SignInFlow.tsx.
//   Hooks sit at the top of each component - never after an early return or inside a condition.
//   One primary button per card; secondary actions are .vsl-btn2 / links.
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { QrCode } from '../../components/security/QrCode';
import { COMMON_PASSWORD_LINE, COMMON_RULE, MIX_TICK, isCommonPassword, mixOk, type CharMix } from '@shared-ui/commonPasswords';   // v12.10.9.4 (#1342); v12.10.11.1 (#1462) COMMON_RULE
import { phoneFromInput } from '../../lib/phone';
import type { CodeChannel, SecondMethod, SetupMethod } from '../../lib/authApi';
import {
  BackLink, Badges, Banner, NewTag, CodeBoxes, CONTACT, CONTACT_LC, FieldError, Form, Head, NameFields, Note, PasswordInput,
  PrimaryButton, SecondaryButton, Sub, mmss, shortDate, useTicker,
} from './parts';

// ═════ L1 / L1a / L2 / L12 · Sign in ═══════════════════════════════════════════════════════════════════════════════
export function SignInView(p: {
  org: string;
  identifier: string; onIdentifier: (v: string) => void;
  password: string; onPassword: (v: string) => void;
  keep: boolean; onKeep: (v: boolean) => void;
  notice: ReactNode;                 // L12 / L1a / "password changed" / outage banner (above the fields)
  mismatch: boolean;                 // L2
  errTick: number;                   // bumps after a server-side failure -> refocus
  busy: boolean; codeBusy: CodeChannel | null;
  onSignIn: () => void; onCode: (channel: CodeChannel) => void; onForgot: () => void;
  onSupport: () => void;             // v12.10.9.1 (#1275)
}) {
  const [fieldErr, setFieldErr] = useState<'id' | 'pw' | null>(null);
  const idRef = useRef<HTMLInputElement>(null);
  const pwRef = useRef<HTMLInputElement>(null);
  const [focusTick, setFocusTick] = useState(0);

  // Focus: first field with an error; after L2 the (cleared) password; on arrival the first empty field.
  useEffect(() => {
    if (fieldErr === 'id') idRef.current?.focus();
    else if (fieldErr === 'pw' || p.mismatch) pwRef.current?.focus();
    else if (p.identifier) pwRef.current?.focus();
    else idRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick, p.errTick]);

  function submit() {
    if (!p.identifier.trim()) { setFieldErr('id'); setFocusTick((t) => t + 1); return; }
    if (!p.password) { setFieldErr('pw'); setFocusTick((t) => t + 1); return; }
    setFieldErr(null);
    p.onSignIn();
  }
  function code(channel: CodeChannel) {
    if (!p.identifier.trim()) { setFieldErr('id'); setFocusTick((t) => t + 1); return; }
    setFieldErr(null);
    p.onCode(channel);
  }
  const what = p.identifier.includes('@') ? 'Email' : 'Mobile number';

  return (
    <Form onSubmit={submit} label="Sign in">
      <Head org={p.org} title="Sign in" rule />
      {p.notice}
      {p.mismatch && <Banner kind="bad">{what} and password don’t match. Try again.</Banner>}
      <div className="tenant-login-fields">
        <div>
          <label htmlFor="vsl-id">Email or mobile number
            <input
              ref={idRef} id="vsl-id" value={p.identifier}
              onChange={(e) => { p.onIdentifier(e.target.value); if (fieldErr === 'id') setFieldErr(null); }}
              autoComplete="username" autoCapitalize="none" spellCheck={false}
              className={fieldErr === 'id' ? 'is-err' : undefined} aria-invalid={fieldErr === 'id' || undefined}
              aria-describedby={fieldErr === 'id' ? 'vsl-id-err' : undefined}
            />
          </label>
          {fieldErr === 'id' && <FieldError id="vsl-id-err">Enter your email or mobile number.</FieldError>}
        </div>
        <div>
          <PasswordInput
            id="vsl-pw" label="Password" value={p.password} inputRef={pwRef}
            onChange={(v) => { p.onPassword(v); if (fieldErr === 'pw') setFieldErr(null); }}
            autoComplete="current-password" invalid={fieldErr === 'pw' || p.mismatch}
            describedBy={fieldErr === 'pw' ? 'vsl-pw-err' : undefined}
          />
          {fieldErr === 'pw' && <FieldError id="vsl-pw-err">Enter your password.</FieldError>}
        </div>
        <div className="tenant-login-row">
          <label className="check-row">
            <input type="checkbox" checked={p.keep} onChange={(e) => p.onKeep(e.target.checked)} />
            <span className="check-box"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg></span>
            Keep me signed in
          </label>
          <button type="button" className="vsl-lnk" onClick={p.onForgot}>Forgot password?</button>
        </div>
      </div>
      <PrimaryButton busy={p.busy} busyText="Signing in…">Sign in</PrimaryButton>
      {/* v12.10.9.3 (#1311): one-time code sign-in went live with 12.10.9 on 2026-10-06 - NEW until 2026-11-05 */}
      <div className="vsl-or">or sign in with a one-time code<NewTag since="2026-10-06" /></div>
      <div className="vsl-two">
        <SecondaryButton onClick={() => code('sms')} disabled={p.busy || p.codeBusy !== null}>{p.codeBusy === 'sms' ? 'Sending…' : 'Text me a code'}</SecondaryButton>
        <SecondaryButton onClick={() => code('email')} disabled={p.busy || p.codeBusy !== null}>{p.codeBusy === 'email' ? 'Sending…' : 'Email me a code'}</SecondaryButton>
      </div>
      {/* v12.10.9.3 (#1312): Contact support and the badges sit below the card now (LoginFrame `below`) */}
    </Form>
  );
}

// ═════ L3 · Locked (live countdown) ═══════════════════════════════════════════════════════════════════════════════
export function LockedView(p: { org: string; until: number; onDone: () => void; onReset: () => void; onBack: () => void }) {
  const now = useTicker(true, 1000);
  const left = Math.max(0, (p.until - now) / 1000);
  const done = left <= 0;
  const { onDone } = p;
  useEffect(() => { if (done) onDone(); }, [done, onDone]);
  return (
    <div>
      <Head org={p.org} title="Account locked" small icon={{ kind: 'bad', glyph: '🔒' }} focusTitle />
      <Sub>Too many attempts. Try again in <b aria-live="off">{mmss(left)}</b>. {CONTACT}</Sub>
      <SecondaryButton onClick={p.onReset}>Reset your password instead</SecondaryButton>
      <BackLink onClick={p.onBack}>Back to sign in</BackLink>
    </div>
  );
}

// ═════ L4 · Text / email code (6 boxes, paste, resend after N s) ═══════════════════════════════════════════════════
export type CodePurpose = 'codelogin' | 'second' | 'setup_sms' | 'reset';
/** v12.10.9.1 (#1288, S47): "Don't ask again on this browser for N days" - under the code on a 2-step screen, ticked by
 *  default so a person on their own computer is asked once a week; untick on a shared front-desk PC. */
export interface TrustChoice { days: number; on: boolean; set: (v: boolean) => void }
export function TrustBox({ trust }: { trust?: TrustChoice }) {
  if (!trust || trust.days <= 0) return null;
  return (
    <label className="check-row vsl-trust">
      <input type="checkbox" checked={trust.on} onChange={(e) => trust.set(e.target.checked)} />
      <span className="check-box"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg></span>
      Don’t ask again on this browser for {trust.days} days
    </label>
  );
}

export function CodeView(p: {
  org: string; purpose: CodePurpose; channel: CodeChannel; masked: string | null;
  typed?: string;                      // v12.10.11.1 (#1470): what the person typed, shown in full (never masked)
  resendAt: number; resent: boolean; error: string | null; errKey: number; busy: boolean; notice?: ReactNode;
  altMethods: SecondMethod[];          // second step only: other ways offered by the server
  onVerify: (code: string) => void; onResend: () => void; onAlt: (m: SecondMethod) => void; onBack?: () => void;
  trust?: TrustChoice;                 // v12.10.9.1 (#1288) second step only
}) {
  const [code, setCode] = useState('');
  const now = useTicker(p.resendAt > Date.now(), 1000);
  const wait = Math.max(0, Math.ceil((p.resendAt - now) / 1000));
  useEffect(() => { setCode(''); }, [p.errKey]);

  const email = p.channel === 'email';
  const title = email ? 'Check your email' : 'Check your phone';
  let sub: ReactNode;
  // v12.10.11.1 (#1470, LOCKED UI-PROTO-T56 v1.16): code sign-in names EXACTLY what was typed and never says "we sent"
  // for an account that may not exist (nothing is sent then) - same sentence for text and email.
  const lookup = p.purpose === 'codelogin' || p.purpose === 'reset';
  if (lookup && p.typed) sub = <>If an account matches <b className="vsl-nowrap">{p.typed}</b>, we’ve {email ? 'emailed' : 'texted'} a 6-digit code. It’s valid for 10 minutes.</>;
  else if (p.purpose === 'reset') sub = 'Type the 6-digit code we texted to the mobile on your account. It works for 10 minutes.';
  else if (p.masked) sub = <>{email ? 'We emailed a 6-digit code to' : 'We texted a 6-digit code to'} <span className="vsl-nowrap">{p.masked}</span>. It’s valid for 10 minutes.</>;
  else sub = `If an account matches, we’ve ${email ? 'emailed' : 'texted'} a 6-digit code. It’s valid for 10 minutes.`;

  return (
    <Form onSubmit={() => { if (code.length === 6) p.onVerify(code); }} label={title}>
      <Head org={p.org} title={title} small />
      {p.notice}
      <Sub>{sub}</Sub>
      <CodeBoxes key={p.errKey} label="6-digit code" invalid={!!p.error} describedBy={p.error ? 'vsl-code-err' : undefined}
        onChange={setCode} onComplete={(c) => { if (!p.busy) p.onVerify(c); }} disabled={p.busy} />
      {p.error && <FieldError id="vsl-code-err"><span className="vsl-center-block">{p.error}</span></FieldError>}
      {p.purpose === 'second' && <TrustBox trust={p.trust} />}
      <PrimaryButton disabled={code.length !== 6} busy={p.busy} busyText="Verifying…">{p.purpose === 'reset' ? 'Continue' : 'Verify'}</PrimaryButton>
      <div className="vsl-row2">
        <button type="button" className="vsl-lnk" disabled={wait > 0} onClick={p.onResend}>Resend code</button>
        <span className="vsl-mut" aria-live="polite">{wait > 0 ? `in ${mmss(wait)}` : p.resent ? 'New code sent' : ''}</span>
      </div>
      {p.purpose === 'second' && p.altMethods.length > 0 && (
        <div className="vsl-alt">
          {p.altMethods.includes('totp') && <button type="button" className="vsl-lnk" onClick={() => p.onAlt('totp')}>Use authenticator app</button>}
          {p.altMethods.includes('backup') && <button type="button" className="vsl-lnk" onClick={() => p.onAlt('backup')}>Use a backup code</button>}
        </div>
      )}
      {lookup
        ? <Note>Didn’t receive the code? Check that your email or mobile number is typed correctly.</Note>
        : <Note>No access to this {email ? 'mailbox' : 'phone'}? {CONTACT}</Note>}
      {p.onBack && <BackLink onClick={p.onBack}>{p.purpose === 'setup_sms' ? 'Choose another way' : 'Back to sign in'}</BackLink>}
    </Form>
  );
}

// ═════ L4a · Authenticator code ════════════════════════════════════════════════════════════════════════════════════
export function TotpView(p: {
  org: string; fromTextCode: boolean; canText: boolean; canBackup: boolean;
  error: string | null; errKey: number; busy: boolean;
  onVerify: (code: string) => void; onText: () => void; onBackup: () => void; onBack: () => void;
  trust?: TrustChoice;   // v12.10.9.1 (#1288)
}) {
  const [code, setCode] = useState('');
  useEffect(() => { setCode(''); }, [p.errKey]);
  return (
    <Form onSubmit={() => { if (code.length === 6) p.onVerify(code); }} label="Enter your authenticator code">
      <Head org={p.org} title="Enter your authenticator code" small />
      {p.fromTextCode && <Banner kind="info">You signed in with a texted code, so your second step has to come from somewhere else.</Banner>}
      <Sub>Open your authenticator app (Google, Microsoft…) and type the 6-digit code for <b>VoiceSpectrum · {p.org}</b>.</Sub>
      <CodeBoxes key={p.errKey} label="6-digit authenticator code" invalid={!!p.error} describedBy={p.error ? 'vsl-code-err' : undefined}
        onChange={setCode} onComplete={(c) => { if (!p.busy) p.onVerify(c); }} disabled={p.busy} />
      {p.error && <FieldError id="vsl-code-err"><span className="vsl-center-block">{p.error}</span></FieldError>}
      <TrustBox trust={p.trust} />
      <PrimaryButton disabled={code.length !== 6} busy={p.busy} busyText="Verifying…">Verify</PrimaryButton>
      {(p.canText || p.canBackup) && (
        <div className="vsl-alt">
          {p.canText && <button type="button" className="vsl-lnk" onClick={p.onText}>Text me a code instead</button>}
          {p.canBackup && <button type="button" className="vsl-lnk" onClick={p.onBackup}>Use a backup code</button>}
        </div>
      )}
      <Note>{p.canBackup ? <>Lost your phone? Use a backup code, or {CONTACT_LC}</> : <>Lost your phone? {CONTACT}</>}</Note>
      <BackLink onClick={p.onBack}>Back to sign in</BackLink>
    </Form>
  );
}

// ═════ L4b · Backup code ═══════════════════════════════════════════════════════════════════════════════════════════
export function BackupView(p: { org: string; error: string | null; errKey: number; busy: boolean; onVerify: (code: string) => void; onBack: () => void; trust?: TrustChoice }) {
  const [code, setCode] = useState('');
  const [empty, setEmpty] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { setCode(''); ref.current?.focus(); }, [p.errKey]);
  const bad = !!p.error || empty;
  return (
    <Form onSubmit={() => { const v = code.trim().toUpperCase(); if (!v) { setEmpty(true); ref.current?.focus(); return; } setEmpty(false); p.onVerify(v); }} label="Use a backup code">
      <Head org={p.org} title="Use a backup code" small />
      <Sub>Type one of the 10 codes you saved when you set up 2-step. Each code works once.</Sub>
      <div className="tenant-login-fields">
        <div>
          <label htmlFor="vsl-bk">Backup code
            <input ref={ref} id="vsl-bk" className={`vsl-mono${bad ? ' is-err' : ''}`} value={code} autoComplete="one-time-code"
              autoCapitalize="characters" spellCheck={false} onChange={(e) => { setCode(e.target.value); setEmpty(false); }}
              aria-invalid={bad || undefined} aria-describedby={bad ? 'vsl-bk-err' : undefined} />
          </label>
          {p.error && <FieldError id="vsl-bk-err">{p.error}</FieldError>}
          {!p.error && empty && <FieldError id="vsl-bk-err">Enter a backup code.</FieldError>}
        </div>
      </div>
      <TrustBox trust={p.trust} />
      <PrimaryButton busy={p.busy} busyText="Checking…">Continue</PrimaryButton>
      <BackLink onClick={p.onBack}>Back</BackLink>
    </Form>
  );
}

// ═════ L5 · Add a second step ══════════════════════════════════════════════════════════════════════════════════════
export function SetupView(p: {
  org: string; canSkip: boolean; graceDaysLeft?: number; enforceOn?: string; methods: SetupMethod[];
  initialMobile: string; error: string | null; busy: boolean; skipBusy: boolean;
  onContinue: (method: SetupMethod, mobile: string) => void; onSkip: () => void;
}) {
  const [method, setMethod] = useState<SetupMethod>(p.methods.includes('totp') ? 'totp' : 'sms');
  const [mobile, setMobile] = useState(p.initialMobile);
  const [mobileErr, setMobileErr] = useState<string | null>(null);
  const mobRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (mobileErr) mobRef.current?.focus(); }, [mobileErr]);
  const err = mobileErr ?? (method === 'sms' ? p.error : null);

  function go() {
    if (method === 'sms') {
      const digits = mobile.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
      if (digits.length !== 10) { setMobileErr('Enter a 10-digit US or Canada mobile number.'); return; }
    }
    setMobileErr(null);
    p.onContinue(method, mobile);
  }
  const days = p.graceDaysLeft ?? 0;
  return (
    <Form onSubmit={go} label="Add a second step">
      <Head org={p.org} title="Add a second step" small />
      {p.canSkip
        ? <Banner kind="warn">{p.org} requires a second step{p.enforceOn ? <> from <b>{shortDate(p.enforceOn)}</b></> : null} — {days} {days === 1 ? 'day' : 'days'} left.</Banner>
        : <Banner kind="bad">{p.org} now requires a second step. Set it up to continue.</Banner>}
      <Sub left>Choose how you’ll prove it’s you after your password:</Sub>
      <div role="radiogroup" aria-label="Second step">
        {p.methods.includes('totp') && (
          <button type="button" role="radio" aria-checked={method === 'totp'} className={`vsl-opt${method === 'totp' ? ' is-on' : ''}`} onClick={() => setMethod('totp')}>
            <span className="vsl-opt-ic" aria-hidden="true">📱</span>
            <span><b>Authenticator app <span className="vsl-tag">Recommended</span></b><span>A 6-digit code from Google or Microsoft Authenticator. Works without phone signal.</span></span>
          </button>
        )}
        {p.methods.includes('sms') && (
          <button type="button" role="radio" aria-checked={method === 'sms'} className={`vsl-opt${method === 'sms' ? ' is-on' : ''}`} onClick={() => setMethod('sms')}>
            <span className="vsl-opt-ic" aria-hidden="true">💬</span>
            <span><b>Text message code</b><span>We text a 6-digit code to your mobile each time.</span></span>
          </button>
        )}
      </div>
      {method === 'sms' && (
        <div className="tenant-login-fields vsl-fields-tight">
          <div>
            <label htmlFor="vsl-setup-mob">Mobile number for codes
              <input ref={mobRef} id="vsl-setup-mob" value={mobile} inputMode="tel" autoComplete="tel"
                onChange={(e) => { setMobile(phoneFromInput(e.target.value, mobile)); setMobileErr(null); }}
                className={err ? 'is-err' : undefined} aria-invalid={!!err || undefined} aria-describedby={err ? 'vsl-setup-err' : undefined} />
            </label>
            {err && <FieldError id="vsl-setup-err">{err}</FieldError>}
          </div>
        </div>
      )}
      {method === 'totp' && p.error && <FieldError>{p.error}</FieldError>}
      <PrimaryButton busy={p.busy} busyText="One moment…">Continue</PrimaryButton>
      {p.canSkip && <SecondaryButton onClick={p.onSkip} disabled={p.busy || p.skipBusy}>{p.skipBusy ? 'One moment…' : 'Remind me later'}</SecondaryButton>}
      <Note>You’ll also get 10 backup codes for when your usual way isn’t available.</Note>
    </Form>
  );
}

// ═════ L5a · Authenticator setup (QR + key + verify) ═══════════════════════════════════════════════════════════════
export function SetupTotpView(p: {
  org: string; secret: string; otpauthUri: string; error: string | null; errKey: number; busy: boolean;
  onVerify: (code: string) => void; onBack: () => void;
}) {
  const [code, setCode] = useState('');
  useEffect(() => { setCode(''); }, [p.errKey]);
  const key = useMemo(() => p.secret.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim(), [p.secret]);
  return (
    <Form onSubmit={() => { if (code.length === 6) p.onVerify(code); }} label="Set up your authenticator app">
      <Head org={p.org} title="Set up your authenticator app" small />
      <Sub left>1 · Open Google Authenticator, Microsoft Authenticator or any authenticator app.<br />2 · Scan this code (or type the key).<br />3 · Type the 6-digit code the app shows.</Sub>
      <div className="vsl-qr"><QrCode text={p.otpauthUri} label="QR code for your authenticator app" /></div>
      <div className="vsl-key" aria-label={`Setup key ${key}`}>{key}</div>
      <CodeBoxes key={p.errKey} label="6-digit code from your authenticator app" invalid={!!p.error} describedBy={p.error ? 'vsl-code-err' : undefined}
        onChange={setCode} onComplete={(c) => { if (!p.busy) p.onVerify(c); }} disabled={p.busy} />
      {p.error && <FieldError id="vsl-code-err"><span className="vsl-center-block">{p.error}</span></FieldError>}
      <PrimaryButton disabled={code.length !== 6} busy={p.busy} busyText="Verifying…">Verify and continue</PrimaryButton>
      <BackLink onClick={p.onBack}>Choose another way</BackLink>
    </Form>
  );
}

// ═════ L5b · Backup codes (shown once) ═════════════════════════════════════════════════════════════════════════════
export function BackupCodesView(p: { org: string; codes: string[]; busy: boolean; onContinue: () => void }) {
  const [saved, setSaved] = useState(false);
  const [dl, setDl] = useState(false);
  const [copied, setCopied] = useState<'no' | 'yes' | 'failed'>('no');
  const text = `VoiceSpectrum backup codes - ${p.org}\n\n${p.codes.join('\n')}\n\nEach code works once. Keep them somewhere safe.\n`;

  function download() {
    try {
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'VoiceSpectrum-backup-codes.txt';
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDl(true);
    } catch { /* download blocked - Copy still works */ }
  }
  function copy() {
    const done = () => setCopied('yes');
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(p.codes.join('\n')).then(done, () => setCopied('failed'));
    else setCopied('failed');
  }
  return (
    <Form onSubmit={() => { if (saved) p.onContinue(); }} label="Save your backup codes">
      <Head org={p.org} title="Save your backup codes" small focusTitle />
      <Sub>If you lose your phone, each code gets you in once. Keep them somewhere safe - they won’t be shown again.</Sub>
      <div className="vsl-codes" aria-label="Backup codes">{p.codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="vsl-two">
        <SecondaryButton onClick={download}>{dl ? 'Downloaded' : 'Download'}</SecondaryButton>
        <SecondaryButton onClick={copy}>{copied === 'yes' ? 'Copied' : copied === 'failed' ? 'Copy didn’t work' : 'Copy'}</SecondaryButton>
      </div>
      <label className="check-row vsl-saved">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span className="check-box"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg></span>
        I’ve saved these codes
      </label>
      <PrimaryButton disabled={!saved} busy={p.busy} busyText="One moment…">Continue</PrimaryButton>
    </Form>
  );
}

// ═════ L13 · Complete your profile ═════════════════════════════════════════════════════════════════════════════════
export function ProfileView(p: {
  org: string; error: string | null; busy: boolean;
  onContinue: (first: string, last: string, mobile: string) => void;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [mobile, setMobile] = useState('');
  const [nameErr, setNameErr] = useState(false);
  const [mobileErr, setMobileErr] = useState<string | undefined>(undefined);
  const [tick, setTick] = useState(0);
  const fRef = useRef<HTMLInputElement>(null);
  const lRef = useRef<HTMLInputElement>(null);
  const mRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (tick === 0) { fRef.current?.focus(); return; }
    if (nameErr && !first.trim()) fRef.current?.focus();
    else if (nameErr && !last.trim()) lRef.current?.focus();
    else if (mobileErr) mRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  function go() {
    const nErr = !first.trim() || !last.trim();
    const mErr = mobile.trim() && mobile.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').length !== 10
      ? 'Enter a 10-digit US or Canada mobile number, or leave it blank.' : undefined;
    setNameErr(nErr); setMobileErr(mErr); setTick((t) => t + 1);
    if (nErr || mErr) return;
    p.onContinue(first.trim(), last.trim(), mobile.trim());
  }
  return (
    <Form onSubmit={go} label="Complete your profile">
      <Head org={p.org} title="Complete your profile" small />
      <Sub>One-time step: VoiceSpectrum now shows who did what, so tell us your name.</Sub>
      {p.error && <Banner kind="bad">{p.error}</Banner>}
      <div className="tenant-login-fields">
        <NameFields first={first} last={last} mobile={mobile}
          onFirst={(v) => { setFirst(v); }} onLast={(v) => { setLast(v); }} onMobile={(v) => { setMobile(phoneFromInput(v, mobile)); setMobileErr(undefined); }}
          nameError={nameErr && (!first.trim() || !last.trim())} mobileError={mobileErr}
          firstRef={fRef} lastRef={lRef} mobileRef={mRef} />
      </div>
      <PrimaryButton busy={p.busy} busyText="Saving…">Continue</PrimaryButton>
    </Form>
  );
}

// ═════ L6 · Forgot password ════════════════════════════════════════════════════════════════════════════════════════
export function ForgotView(p: { org: string; initial: string; notice?: ReactNode; busy: boolean; onSend: (identifier: string) => void; onBack: () => void }) {
  const [id, setId] = useState(p.initial);
  const [err, setErr] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, [err]);
  return (
    <Form onSubmit={() => { if (!id.trim()) { setErr(true); ref.current?.focus(); return; } p.onSend(id.trim()); }} label="Reset your password">
      <Head org={p.org} title="Reset your password" small />
      {p.notice}
      <Sub>Enter the email or mobile you sign in with.</Sub>
      <div className="tenant-login-fields">
        <div>
          <label htmlFor="vsl-fid">Email or mobile number
            <input ref={ref} id="vsl-fid" value={id} onChange={(e) => { setId(e.target.value); setErr(false); }}
              autoComplete="username" autoCapitalize="none" spellCheck={false} className={err ? 'is-err' : undefined}
              aria-invalid={err || undefined} aria-describedby={err ? 'vsl-fid-err' : undefined} />
          </label>
          {err && <FieldError id="vsl-fid-err">Enter your email or mobile number.</FieldError>}
        </div>
      </div>
      <PrimaryButton busy={p.busy} busyText="Sending…">Send reset</PrimaryButton>
      <BackLink onClick={p.onBack}>Back to sign in</BackLink>
    </Form>
  );
}

// ═════ #1275 · Contact support (UI-LOCK-12.10.9.1 v1.2, settled 10/6) ═════════════════════════════════════════════
// Four fields, two required (email-or-mobile, message). Practice is filled when the page knows it.
export interface SupportFields { name: string; practice: string; contact: string; message: string }
// v12.10.30 (#2255): backText - the practice-offline page opens this same form; its way back is not "Back to sign in".
export function SupportView(p: { org: string; initial: SupportFields; notice?: ReactNode; busy: boolean; onSend: (f: SupportFields) => void; onBack: () => void; backText?: string }) {
  const [f, setF] = useState<SupportFields>(p.initial);
  const [err, setErr] = useState<'contact' | 'message' | null>(null);
  const contactRef = useRef<HTMLInputElement>(null);
  const msgRef = useRef<HTMLTextAreaElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (err === 'contact') contactRef.current?.focus();
    else if (err === 'message') msgRef.current?.focus();
    else nameRef.current?.focus();
  }, [err]);
  const set = (k: keyof SupportFields, v: string) => { setF((x) => ({ ...x, [k]: v })); if (err === k) setErr(null); };
  function submit() {
    if (!f.contact.trim()) { setErr('contact'); return; }
    if (!f.message.trim()) { setErr('message'); return; }
    setErr(null);
    p.onSend({ name: f.name.trim(), practice: f.practice.trim(), contact: f.contact.trim(), message: f.message.trim() });
  }
  return (
    <Form onSubmit={submit} label="Contact support">
      <Head org={p.org} title="Contact support" small />
      {p.notice}
      <Sub>Tell us what’s wrong - we’ll get back to you.</Sub>
      <div className="tenant-login-fields">
        <label htmlFor="vsl-sname">Your name
          <input ref={nameRef} id="vsl-sname" value={f.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" maxLength={200} />
        </label>
        <label htmlFor="vsl-sprac">Practice
          <input id="vsl-sprac" value={f.practice} onChange={(e) => set('practice', e.target.value)} autoComplete="organization" maxLength={200} />
        </label>
        <div>
          <label htmlFor="vsl-scontact">Email or mobile *
            <input ref={contactRef} id="vsl-scontact" value={f.contact} onChange={(e) => set('contact', e.target.value)}
              autoComplete="username" autoCapitalize="none" spellCheck={false} maxLength={320}
              className={err === 'contact' ? 'is-err' : undefined} aria-invalid={err === 'contact' || undefined}
              aria-describedby={err === 'contact' ? 'vsl-scontact-err' : undefined} />
          </label>
          {err === 'contact' && <FieldError id="vsl-scontact-err">Enter your email or mobile so support can reply.</FieldError>}
        </div>
        <div>
          <label htmlFor="vsl-smsg">What’s happening *
            <textarea ref={msgRef} id="vsl-smsg" value={f.message} onChange={(e) => set('message', e.target.value)} rows={4} maxLength={2000}
              placeholder="I can’t receive the email code…" className={`vsl-textarea${err === 'message' ? ' is-err' : ''}`}
              aria-invalid={err === 'message' || undefined} aria-describedby={err === 'message' ? 'vsl-smsg-err' : undefined} />
          </label>
          {err === 'message' && <FieldError id="vsl-smsg-err">Tell us what’s happening.</FieldError>}
        </div>
      </div>
      <PrimaryButton busy={p.busy} busyText="Sending…">Send to support</PrimaryButton>
      <BackLink onClick={p.onBack}>{p.backText ?? 'Back to sign in'}</BackLink>
    </Form>
  );
}

// v12.10.11.1 (#1460, UI-PROTO-T56 v1.25 f1 / f2): 'existing' = the same email / mobile already has an open request - its
// reference, an optional box and "Add to SR-xxxx" (no new request); 'limited' = 3 today from this email / mobile - nothing stored;
// 'added' = the text joined the open request.
export type SupportOutcome = 'created' | 'existing' | 'limited' | 'added';
export function SupportSentView(p: { org: string; contact: string; reference: string; onBack: () => void;
  mode?: SupportOutcome; sentAt?: string | null; count?: number; draft?: string; busy?: boolean; notice?: ReactNode; onAdd?: (text: string) => void;
  backText?: string }) {   // v12.10.30 (#2255)
  const mode = p.mode ?? 'created';
  const [text, setText] = useState(p.draft ?? '');
  if (mode === 'limited') return (
    <div>
      <Head org={p.org} title="We have your requests" small focusTitle />
      <Sub>You have sent {p.count ?? 3} requests today. Our support team has them all and will reply to <b>{p.contact}</b>.</Sub>
      <Note>Urgent? Call the practice directly.</Note>
      <BackLink onClick={p.onBack}>{p.backText ?? 'Back to sign in'}</BackLink>
    </div>
  );
  if (mode === 'existing') {
    const at = p.sentAt ? new Date(p.sentAt) : null;
    const today = at ? at.toDateString() === new Date().toDateString() : false;
    const when = at && !isNaN(at.getTime())
      ? `${today ? 'today' : at.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : '';
    return (
      <Form onSubmit={() => { if (text.trim() && p.onAdd) p.onAdd(text.trim()); }} label="We already have your request">
        <Head org={p.org} title="We already have your request" small focusTitle />
        {p.notice}
        <Sub>Reference <b>{p.reference}</b>{when ? `, sent ${when}` : ''}. Support will reply to <b>{p.contact}</b>.</Sub>
        <div className="tenant-login-fields">
          <label htmlFor="vsl-sadd">Anything to add? <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span>
            <textarea id="vsl-sadd" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} className="vsl-textarea" />
          </label>
        </div>
        <PrimaryButton busy={p.busy} busyText="Adding…" disabled={!text.trim()}>{`Add to ${p.reference}`}</PrimaryButton>
        <BackLink onClick={p.onBack}>{p.backText ?? 'Back to sign in'}</BackLink>
      </Form>
    );
  }
  return (
    <div>
      <Head org={p.org} title={mode === 'added' ? `Added to ${p.reference}` : 'We got it'} small icon={{ kind: 'ok', glyph: '✓' }} focusTitle />
      <Sub>Support will reply to <b>{p.contact}</b>. Reference <b>{p.reference}</b>.</Sub>
      <BackLink onClick={p.onBack}>{p.backText ?? 'Back to sign in'}</BackLink>
    </div>
  );
}

// ═════ L6b · Sent ══════════════════════════════════════════════════════════════════════════════════════════════════
export function ForgotSentView(p: { org: string; via: 'email' | 'sms'; typed?: string; onEnterCode: () => void; onBack: () => void }) {
  const email = p.via === 'email';
  return (
    <div>
      <Head org={p.org} title={email ? 'Check your email' : 'Check your phone'} small icon={{ kind: 'ok', glyph: '✓' }} focusTitle />
      {/* v12.10.11.1 (#1470): what was typed, in full; the typed-correctly hint replaces silence */}
      <Sub>If an account matches {p.typed ? <b className="vsl-nowrap">{p.typed}</b> : null}{p.typed ? ', ' : ', '}we’ve {email ? 'emailed a link to reset your password. It works for 30 minutes.' : 'texted a 6-digit code. It’s valid for 10 minutes.'}</Sub>
      {!email && <PrimaryButton type="button" onClick={p.onEnterCode}>Enter the code</PrimaryButton>}
      <Note>Didn’t receive the {email ? 'link' : 'code'}? Check that your email or mobile number is typed correctly.</Note>
      <BackLink onClick={p.onBack}>Back to sign in</BackLink>
    </div>
  );
}

// ═════ L7 / L10 · New password (reset · required · invite) with live rules ═════════════════════════════════════════
export type PwPurpose = 'reset_token' | 'reset_code' | 'required' | 'invite';
export function NewPasswordView(p: {
  org: string; purpose: PwPurpose; minLength: number; reuse: number;
  charMix?: CharMix; context?: (string | null | undefined)[];   // v12.10.9.4 (#1342 F9)
  personal: string[];                  // things the password must not contain (email, name…) - known so far
  rejections: { password: string; failed: string[] }[];   // what the server refused (400 rules), per password tried
  needCurrent: boolean;                // L8 after a code sign-in: we don't hold the old password
  invite?: { groupNames: string[]; identifier: string };
  // v12.10.11.1 (#1462, UI-PROTO-T56 SETTLED A): what the inviter / Step 5 already entered - the person can still correct it.
  prefill?: { first?: string | null; last?: string | null; mobile?: string | null };
  error: ReactNode; busy: boolean;
  onSave: (v: { password: string; current: string; first: string; last: string; mobile: string }) => void;
}) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [current, setCurrent] = useState('');
  const [first, setFirst] = useState(() => (p.prefill?.first ?? '').trim());
  const [last, setLast] = useState(() => (p.prefill?.last ?? '').trim());
  const [mobile, setMobile] = useState(() => (p.prefill?.mobile ? phoneFromInput(p.prefill.mobile, '') : ''));
  const [mobileErr, setMobileErr] = useState<string | undefined>(undefined);
  const firstRef = useRef<HTMLInputElement>(null);
  const pwRef = useRef<HTMLInputElement>(null);
  const curRef = useRef<HTMLInputElement>(null);
  const mRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (p.purpose === 'invite') firstRef.current?.focus();
    else if (p.needCurrent) curRef.current?.focus();
    else pwRef.current?.focus();
  }, [p.purpose, p.needCurrent]);
  useEffect(() => { if (mobileErr) mRef.current?.focus(); }, [mobileErr]);

  const invite = p.purpose === 'invite';
  const low = pw1.toLowerCase();
  const personal = [...p.personal, first, last].map((s) => s.trim().toLowerCase()).filter((s) => s.length >= 3);
  // The server is the judge of "email or name" (it knows the account) and the only judge of "last N"; a rule it failed
  // for this exact password shows ✕ until the password changes.
  const serverFailed = (keys: string[]) => p.rejections.some((r) => r.password === pw1 && r.failed.some((f) => keys.some((k) => f.toLowerCase().includes(k))));
  // v12.10.9.4 (#1342 F9, UI-PROTO-T40 LOCKED 10/7): the common / leaked check and the character mix tick too; the common
  // check reads the password after a short pause, like the other ticks it never blocks typing.
  const [pwSettled, setPwSettled] = useState(pw1);
  useEffect(() => { const t = window.setTimeout(() => setPwSettled(pw1), 350); return () => window.clearTimeout(t); }, [pw1]);
  const mix: CharMix = p.charMix ?? 'any';
  const common = pwSettled.length > 0 && isCommonPassword(pwSettled, [...(p.context ?? []), ...p.personal]);
  const rules: { text: string; ok: boolean }[] = [
    { text: `At least ${p.minLength} characters${pw1 && pw1.length < p.minLength ? ` (${pw1.length})` : ''}`, ok: pw1.length >= p.minLength && !serverFailed(['length', 'min', 'short']) },
    { text: 'Not the same as your email or name', ok: pw1.length > 0 && !personal.some((t) => low.includes(t)) && !serverFailed(['email', 'name', 'personal', 'identifier', 'mobile']) },
    { text: COMMON_RULE, ok: pw1.length > 0 && !common && !serverFailed(['common', 'leak']) },
  ];
  if (mix !== 'any') rules.push({ text: MIX_TICK[mix], ok: pw1.length > 0 && mixOk(pw1, mix) && !serverFailed(['upper', 'letter', 'symbol', 'complex']) });
  if (p.reuse > 0) rules.push({ text: `Not one of your last ${p.reuse} passwords`, ok: pw1.length > 0 && !serverFailed(['reuse', 'history', 'last', 'previous', 'recent']) });
  const score = rules.filter((r) => r.ok).length;
  const allOk = score === rules.length;
  const match = pw2.length > 0 && pw1 === pw2;
  const namesOk = !invite || (first.trim() !== '' && last.trim() !== '');
  const canSave = allOk && match && namesOk && (!p.needCurrent || current.length > 0);
  const meterColor = score >= rules.length ? '#16a34a' : score >= rules.length - 1 ? '#d97706' : '#dc2626';
  const meterWidth = pw1 ? Math.round((score / rules.length) * 100) : 0;

  // v12.10.11.1 (#1462, UI-PROTO-T56 heading A LOCKED): "Set up your login" and no grey line under it.
  const title = invite ? 'Set up your login' : p.purpose === 'required' ? 'Set a new password to continue' : 'Choose a new password';

  function save() {
    if (!canSave) return;
    if (invite && mobile.trim() && mobile.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').length !== 10) {
      setMobileErr('Enter a 10-digit US or Canada mobile number, or leave it blank.');
      return;
    }
    p.onSave({ password: pw1, current, first: first.trim(), last: last.trim(), mobile: mobile.trim() });
  }

  return (
    <Form onSubmit={save} label={title}>
      <Head org={p.org} title={title} small />
      {/* v12.10.9.1 (#1270, Tushar 10/6 option 2): no group on the accept page - a person can be in several, which only confuses. */}
      {p.error}
      <div className={`tenant-login-fields vsl-pwform${invite ? ' vsl-pwform--setup' : ''}`}>
        {invite && (
          <NameFields first={first} last={last} mobile={mobile} onFirst={setFirst} onLast={setLast}
            onMobile={(v) => { setMobile(phoneFromInput(v, mobile)); setMobileErr(undefined); }}
            nameError={false} mobileError={mobileErr} firstRef={firstRef} mobileRef={mRef} />
        )}
        {p.needCurrent && (
          <PasswordInput id="vsl-cur" label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" inputRef={curRef} />
        )}
        {/* v12.10.11.1 (#1462, UI-PROTO-T56 LOCKED): Set up your login - Create a password and Confirm password full width,
            one under the other, both shown from the start; reset - New and Confirm side by side (stacked on a phone), the
            strength bar under New only. The rules, one per line, and the red line go under BOTH boxes; a green ✓ inside
            Confirm, just before Show, once it matches. */}
        <div className={invite ? 'vsl-pwstack' : 'vsl-pwpair2'}>
          <div>
            <PasswordInput id="vsl-np1" label={invite ? 'Create a password' : 'New password'} value={pw1} onChange={setPw1}
              autoComplete="new-password" inputRef={pwRef} describedBy="vsl-rules" />
            <div className="vsl-meter" aria-hidden="true"><i style={{ width: `${meterWidth}%`, background: meterColor }} /></div>
          </div>
          <div>
            <PasswordInput id="vsl-np2" label="Confirm password" value={pw2} onChange={setPw2} autoComplete="new-password"
              invalid={pw2.length > 0 && !match} describedBy={pw2 && !match ? 'vsl-mm' : undefined} match={match} />
            {pw2.length > 0 && !match && <FieldError id="vsl-mm">Passwords don’t match.</FieldError>}
          </div>
        </div>
        <div>
          <ul className="vsl-rules vsl-rules--lines" id="vsl-rules" aria-label="Password rules">
            {rules.map((r) => (
              <li key={r.text.replace(/ \(\d+\)$/, '')} className={pw1 ? (r.ok ? 'is-ok' : 'is-no') : undefined}>
                {r.text}<span className="vsl-sr">{pw1 ? (r.ok ? ' - met' : ' - not met') : ''}</span>
              </li>
            ))}
          </ul>
          {common && <FieldError id="vsl-common">{COMMON_PASSWORD_LINE}</FieldError>}
        </div>
      </div>
      <PrimaryButton disabled={!canSave} busy={p.busy} busyText="Saving…">{invite ? 'Create account' : 'Save password'}</PrimaryButton>
      {invite && p.invite?.identifier && <Note>You’ll sign in with {p.invite.identifier}</Note>}
    </Form>
  );
}

// ═════ L8 · Password change required ═══════════════════════════════════════════════════════════════════════════════
export function RequiredView(p: { org: string; onContinue: () => void }) {
  return (
    <div>
      <Head org={p.org} title="Set a new password to continue" small icon={{ kind: 'info', glyph: '⟳' }} focusTitle />
      <Banner kind="info">Your account was turned back on after a long break, so you need a new password and to confirm your phone.</Banner>
      <PrimaryButton type="button" onClick={p.onContinue}>Continue</PrimaryButton>
    </div>
  );
}

// ═════ L9 · Disabled ═══════════════════════════════════════════════════════════════════════════════════════════════
export function DisabledView(p: { org: string; onBack: () => void }) {
  return (
    <div>
      <Head org={p.org} title="Your access is turned off" small icon={{ kind: 'bad', glyph: '⊘' }} focusTitle />
      <Sub>This account has been disabled. {CONTACT}</Sub>
      <BackLink onClick={p.onBack}>Back to sign in</BackLink>
    </div>
  );
}

// ═════ L11 · Invite expired / invalid (and the invite lookup failing) ══════════════════════════════════════════════
export function InviteProblemView(p: { org: string; kind: 'expired' | 'invalid' | 'error'; onSignIn: () => void; onRetry: () => void }) {
  const title = p.kind === 'expired' ? 'This invite has expired' : p.kind === 'invalid' ? 'This invite is no longer valid' : 'We couldn’t open this invite';
  return (
    <div>
      <Head org={p.org} title={title} small icon={{ kind: p.kind === 'error' ? 'bad' : 'info', glyph: p.kind === 'error' ? '!' : '⏱' }} focusTitle />
      <Sub>{p.kind === 'expired' ? <>Invites last 7 days. {CONTACT}</> : p.kind === 'invalid' ? CONTACT : 'VoiceSpectrum is temporarily unavailable. Please try again in a moment.'}</Sub>
      {p.kind === 'error' && <SecondaryButton onClick={p.onRetry}>Try again</SecondaryButton>}
      <SecondaryButton onClick={p.onSignIn}>Go to sign in</SecondaryButton>
    </div>
  );
}

export function LoadingView({ org }: { org: string }) {
  return (
    <div aria-busy="true">
      <Head org={org} title="One moment…" small />
      <Sub>Opening your invite.</Sub>
    </div>
  );
}
